/**
 * Public service for fetching data without authentication.
 * These methods use the Supabase anon key and rely on RLS policies
 * that allow public access to published courses.
 */

import { Course, Section, Lesson, Lead, CreateLeadRequest, CreateLeadResponse, LeadSource, UtilityMetadata, Tag } from '../types';
import { supabase } from '../lib/supabase';

export interface PublicCourse {
  id: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  /**
   * Undefined when the reader cannot resolve the instructor — which for an
   * anonymous visitor is ALWAYS: there is no anon SELECT policy on `users`, so
   * the join returns null on every course of every tenant. The catalogue paths
   * below deliberately leave it undefined rather than falling back to the
   * literal "Unknown" that logged-out visitors used to be shown.
   */
  instructorName?: string;
  price: number;
  category: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  totalStudents: number;
  rating: number;
  /** Course opts into showing its facilitator on the detail page (default off). */
  showFacilitator?: boolean;
  tags?: Tag[];
}

// Category order first, then tag order — tag sort_order is a per-category
// sequence, so sorting by it alone interleaves categories arbitrarily.
// Mirrors mapCourseTags in courseService.
function mapPublicCourseTags(rows: any): Tag[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row: any) => row?.tag)
    .filter(Boolean)
    .map((t: any) => ({
      id: t.id,
      categoryId: t.category_id,
      tenantId: t.tenant_id,
      name: t.name,
      color: t.color ?? undefined,
      sortOrder: t.sort_order ?? 0,
      categorySortOrder: t.category?.sort_order ?? 0,
    }))
    .sort(
      (a, b) =>
        a.categorySortOrder - b.categorySortOrder ||
        a.sortOrder - b.sortOrder ||
        a.name.localeCompare(b.name),
    )
    .map(({ categorySortOrder: _cso, ...tag }) => tag);
}

export interface PublicCourseDetail extends PublicCourse {
  sections: PublicSection[];
}

/** A bundle as an anonymous visitor sees it on the marketing catalogue. */
export interface PublicBundle {
  id: string;
  title: string;
  description: string;
  thumbnailUrl?: string;
  price: number;
  accessDurationDays?: number;
  courses: PublicBundleCourse[];
}

export interface PublicBundleCourse {
  id: string;
  title: string;
  thumbnailUrl: string;
  price: number;
  sortOrder: number;
}

export interface PublicSection {
  id: string;
  title: string;
  order: number;
  lessons: PublicLesson[];
}

export interface PublicLesson {
  id: string;
  title: string;
  type: string;
  duration: number;
  isPreview: boolean;
  order: number;
}

// --- Utility Page Types ---

export interface UtilityPageSummary {
  id: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  slug: string;
  category: string;
  instructorName: string;
  slideCount: number;
  estimatedMinutes: number;
  parentCourseId?: string;
  parentCourseTitle?: string;
}

export interface UtilityPageDetail extends UtilityPageSummary {
  utilityMetadata: UtilityMetadata;
  sections: PublicSection[];
  // Flattened slides content for the viewer (fetched from lessons)
  lessonContent: Array<{
    lessonId: string;
    title: string;
    content: unknown; // SlideBasedContent | LessonContent | string
  }>;
}

export const publicService = {
  /**
   * Get all published courses (no auth required)
   * @param tenantId - If provided, filters courses to this tenant
   */
  async getPublicCourses(tenantId?: string): Promise<PublicCourse[]> {
    let query = supabase
      .from('courses')
      .select(`
        id,
        title,
        description,
        thumbnail_url,
        price,
        category,
        level,
        total_students,
        rating,
        show_facilitator,
        instructor:users!instructor_id(name),
        course_tags(tag:tags(*, category:tag_categories(sort_order)))
      `)
      .eq('published', true)
      .or('course_type.eq.standard,course_type.is.null')
      .order('total_students', { ascending: false });

    if (tenantId) {
      query = query.eq('tenant_id', tenantId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('getPublicCourses error:', error);
      throw new Error(error.message);
    }

    return (data || []).map(course => ({
      id: course.id,
      title: course.title,
      description: course.description,
      thumbnailUrl: course.thumbnail_url,
      instructorName: (course.instructor as any)?.name || undefined,
      price: course.price,
      category: course.category,
      level: course.level,
      totalStudents: course.total_students,
      rating: course.rating,
      showFacilitator: (course as any).show_facilitator ?? false,
      tags: mapPublicCourseTags((course as any).course_tags),
    }));
  },

  /**
   * Get a single published course with curriculum outline (no auth required).
   * Lesson outline is fetched from the column-restricted `public_lesson_outline`
   * view — anon visitors cannot read the full lessons table (which holds the
   * gated `content` JSONB). See migration 20260426000002.
   */
  /**
   * Published bundles for the marketing catalogue, readable without auth.
   *
   * Anon access is granted by 20260809000002; before it this returned zero
   * rows for every visitor, which is why bundles were invisible on the public
   * shelf. Member courses come back via the same anon course policy the rest
   * of this service relies on, so an unpublished member is simply absent.
   */
  async getPublicBundles(tenantId?: string): Promise<PublicBundle[]> {
    let query = supabase
      .from('bundles')
      .select(`
        id,
        title,
        description,
        thumbnail_url,
        price,
        access_duration_days,
        bundle_courses (
          course_id,
          sort_order,
          courses ( id, title, thumbnail_url, price )
        )
      `)
      .eq('published', true)
      .order('sort_order', { ascending: true });

    if (tenantId) {
      query = query.eq('tenant_id', tenantId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('getPublicBundles error:', error);
      throw new Error(error.message);
    }

    return (data || []).map((row: any) => ({
      id: row.id,
      title: row.title,
      description: row.description || '',
      thumbnailUrl: row.thumbnail_url || undefined,
      price: Number(row.price) || 0,
      accessDurationDays: row.access_duration_days ?? undefined,
      courses: (row.bundle_courses || [])
        // A member the visitor can't see (unpublished course) comes back with a
        // null join — drop it rather than rendering a blank row.
        .filter((bc: any) => bc.courses)
        .map((bc: any) => ({
          id: bc.courses.id,
          title: bc.courses.title,
          thumbnailUrl: bc.courses.thumbnail_url || '',
          price: Number(bc.courses.price) || 0,
          sortOrder: bc.sort_order ?? 0,
        }))
        .sort((a: PublicBundleCourse, b: PublicBundleCourse) => a.sortOrder - b.sortOrder),
    }));
  },

  async getPublicCourseById(courseId: string): Promise<PublicCourseDetail | null> {
    // `*` rather than an explicit column list: naming a column makes the query
    // fail hard if the code deploys before its migration (Vercel and `db push`
    // are not atomic), and the courses table holds nothing anon-sensitive —
    // the column restriction that matters is on LESSONS, handled by the
    // public_lesson_outline view below.
    const { data, error } = await supabase
      .from('courses')
      .select(`
        *,
        instructor:users!instructor_id(name),
        sections(id, title, order)
      `)
      .eq('id', courseId)
      .eq('published', true)
      .single();

    if (error) {
      console.error('getPublicCourseById error:', error);
      if (error.code === 'PGRST116') return null;
      throw new Error(error.message);
    }

    const sectionList = (data.sections ?? []).sort((a: any, b: any) => a.order - b.order);
    const sectionIds = sectionList.map((s: any) => s.id);

    let lessonsBySection: Record<string, any[]> = {};
    if (sectionIds.length > 0) {
      const { data: lessons, error: lessonErr } = await supabase
        .from('public_lesson_outline')
        .select('id, section_id, title, type, duration, "order"')
        .in('section_id', sectionIds);
      if (lessonErr) {
        console.error('public_lesson_outline error:', lessonErr);
      } else {
        for (const l of lessons ?? []) {
          (lessonsBySection[l.section_id] ??= []).push(l);
        }
        for (const sid of Object.keys(lessonsBySection)) {
          lessonsBySection[sid].sort((a, b) => a.order - b.order);
        }
      }
    }

    return {
      id: data.id,
      title: data.title,
      description: data.description,
      thumbnailUrl: data.thumbnail_url,
      instructorName: (data.instructor as any)?.name || undefined,
      price: data.price,
      category: data.category,
      level: data.level,
      totalStudents: data.total_students,
      rating: data.rating,
      showFacilitator: data.show_facilitator ?? false,
      sections: sectionList.map((section: any) => ({
        id: section.id,
        title: section.title,
        order: section.order,
        lessons: (lessonsBySection[section.id] ?? []).map((lesson: any) => ({
          id: lesson.id,
          title: lesson.title,
          type: lesson.type,
          duration: lesson.duration,
          isPreview: false, // is_preview not exposed via the public view
          order: lesson.order,
        })),
      })),
    };
  },

  /**
   * Get featured courses (top by student count)
   */
  async getFeaturedCourses(limit: number = 6, tenantId?: string): Promise<PublicCourse[]> {
    let query = supabase
      .from('courses')
      .select(`
        id,
        title,
        description,
        thumbnail_url,
        price,
        category,
        level,
        total_students,
        rating,
        instructor:users!instructor_id(name)
      `)
      .eq('published', true)
      .or('course_type.eq.standard,course_type.is.null')
      .order('total_students', { ascending: false })
      .limit(limit);

    if (tenantId) {
      query = query.eq('tenant_id', tenantId);
    }

    const { data, error } = await query;

    if (error) throw new Error(error.message);

    return (data || []).map(course => ({
      id: course.id,
      title: course.title,
      description: course.description,
      thumbnailUrl: course.thumbnail_url,
      instructorName: (course.instructor as any)?.name || 'Unknown',
      price: course.price,
      category: course.category,
      level: course.level,
      totalStudents: course.total_students,
      rating: course.rating
    }));
  },

  /**
   * Get unique categories from published courses
   */
  async getCategories(tenantId?: string): Promise<string[]> {
    let query = supabase
      .from('courses')
      .select('category')
      .eq('published', true);

    if (tenantId) {
      query = query.eq('tenant_id', tenantId);
    }

    const { data, error } = await query;

    if (error) throw new Error(error.message);

    const categories = [...new Set((data || []).map(c => c.category))];
    return categories.sort();
  },

  /**
   * Search published courses by title or description
   */
  async searchCourses(query: string, tenantId?: string): Promise<PublicCourse[]> {
    let q = supabase
      .from('courses')
      .select(`
        id,
        title,
        description,
        thumbnail_url,
        price,
        category,
        level,
        total_students,
        rating,
        instructor:users!instructor_id(name)
      `)
      .eq('published', true)
      .or('course_type.eq.standard,course_type.is.null')
      .or(`title.ilike.%${query}%,description.ilike.%${query}%`)
      .order('total_students', { ascending: false });

    if (tenantId) {
      q = q.eq('tenant_id', tenantId);
    }

    const { data, error } = await q;

    if (error) throw new Error(error.message);

    return (data || []).map(course => ({
      id: course.id,
      title: course.title,
      description: course.description,
      thumbnailUrl: course.thumbnail_url,
      instructorName: (course.instructor as any)?.name || 'Unknown',
      price: course.price,
      category: course.category,
      level: course.level,
      totalStudents: course.total_students,
      rating: course.rating
    }));
  },

  /**
   * Get courses filtered by category
   */
  async getCoursesByCategory(category: string, tenantId?: string): Promise<PublicCourse[]> {
    let query = supabase
      .from('courses')
      .select(`
        id,
        title,
        description,
        thumbnail_url,
        price,
        category,
        level,
        total_students,
        rating,
        instructor:users!instructor_id(name)
      `)
      .eq('published', true)
      .or('course_type.eq.standard,course_type.is.null')
      .eq('category', category)
      .order('total_students', { ascending: false });

    if (tenantId) {
      query = query.eq('tenant_id', tenantId);
    }

    const { data, error } = await query;

    if (error) throw new Error(error.message);

    return (data || []).map(course => ({
      id: course.id,
      title: course.title,
      description: course.description,
      thumbnailUrl: course.thumbnail_url,
      instructorName: (course.instructor as any)?.name || 'Unknown',
      price: course.price,
      category: course.category,
      level: course.level,
      totalStudents: course.total_students,
      rating: course.rating
    }));
  },

  /**
   * Create a lead for email capture (no auth required)
   * Used for free course access and newsletter signups
   */
  async createLead(request: CreateLeadRequest & { tenantId?: string }): Promise<CreateLeadResponse> {
    const { email, name, source, sourceCourseId, tenantId } = request as CreateLeadRequest & { tenantId?: string };

    // Check if lead already exists
    const { data: existingLead } = await supabase
      .from('leads')
      .select('*')
      .eq('email', email.toLowerCase())
      .single();

    if (existingLead) {
      // Lead exists - return it (they can still access the course)
      return {
        lead: {
          id: existingLead.id,
          email: existingLead.email,
          name: existingLead.name,
          source: existingLead.source as LeadSource,
          sourceCourseId: existingLead.source_course_id,
          convertedUserId: existingLead.converted_user_id,
          createdAt: new Date(existingLead.created_at),
          convertedAt: existingLead.converted_at ? new Date(existingLead.converted_at) : undefined
        },
        isNewLead: false
      };
    }

    // Create new lead
    const { data: newLead, error } = await supabase
      .from('leads')
      .insert({
        email: email.toLowerCase(),
        name: name || null,
        source,
        source_course_id: sourceCourseId || null,
        ...(tenantId ? { tenant_id: tenantId } : {}),
      })
      .select()
      .single();

    if (error) {
      console.error('createLead error:', error);
      throw new Error(error.message);
    }

    return {
      lead: {
        id: newLead.id,
        email: newLead.email,
        name: newLead.name,
        source: newLead.source as LeadSource,
        sourceCourseId: newLead.source_course_id,
        createdAt: new Date(newLead.created_at)
      },
      isNewLead: true
    };
  },

  /**
   * Check if an email already exists as a lead or user
   */
  async checkEmailExists(email: string): Promise<{ isLead: boolean; isUser: boolean }> {
    const normalizedEmail = email.toLowerCase();

    // Check leads table
    const { data: lead } = await supabase
      .from('leads')
      .select('id')
      .eq('email', normalizedEmail)
      .single();

    // Check users table
    const { data: user } = await supabase
      .from('users')
      .select('id')
      .eq('email', normalizedEmail)
      .single();

    return {
      isLead: !!lead,
      isUser: !!user
    };
  },

  // =====================
  // UTILITY PAGE METHODS
  // =====================

  /**
   * Get all published utility pages
   */
  async getUtilityPages(): Promise<UtilityPageSummary[]> {
    const { data, error } = await supabase
      .from('courses')
      .select(`
        id,
        title,
        description,
        thumbnail_url,
        slug,
        category,
        utility_metadata,
        instructor:users!instructor_id(name),
        sections(
          lessons(id)
        )
      `)
      .eq('published', true)
      .eq('course_type', 'utility')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('getUtilityPages error:', error);
      throw new Error(error.message);
    }

    return (data || []).map(page => {
      const meta = page.utility_metadata as any;
      const totalSlides = (page.sections || []).reduce(
        (sum: number, s: any) => sum + (s.lessons?.length || 0), 0
      );
      return {
        id: page.id,
        title: page.title,
        description: page.description,
        thumbnailUrl: page.thumbnail_url,
        slug: page.slug || page.id,
        category: page.category,
        instructorName: (page.instructor as any)?.name || 'Unknown',
        slideCount: totalSlides,
        estimatedMinutes: Math.max(1, Math.ceil(totalSlides * 0.7)),
        parentCourseId: meta?.parentCourseId,
        parentCourseTitle: undefined, // Populated by caller if needed
      };
    });
  },

  /**
   * Get a single utility page by slug with full content (all lessons are preview)
   */
  async getUtilityPageBySlug(slug: string): Promise<UtilityPageDetail | null> {
    const { data, error } = await supabase
      .from('courses')
      .select(`
        id,
        title,
        description,
        thumbnail_url,
        slug,
        category,
        utility_metadata,
        instructor:users!instructor_id(name),
        sections(
          id,
          title,
          order,
          lessons(
            id,
            title,
            duration,
            content,
            order
          )
        )
      `)
      .eq('slug', slug)
      .eq('published', true)
      .eq('course_type', 'utility')
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('getUtilityPageBySlug error:', error);
      throw new Error(error.message);
    }

    const meta = (data.utility_metadata || {}) as any;
    const sections = (data.sections || [])
      .sort((a: any, b: any) => a.order - b.order);

    const lessonContent = sections.flatMap((section: any) =>
      (section.lessons || [])
        .sort((a: any, b: any) => a.order - b.order)
        .map((lesson: any) => ({
          lessonId: lesson.id,
          title: lesson.title,
          content: lesson.content,
        }))
    );

    const totalSlides = lessonContent.length;

    return {
      id: data.id,
      title: data.title,
      description: data.description,
      thumbnailUrl: data.thumbnail_url,
      slug: data.slug || data.id,
      category: data.category,
      instructorName: (data.instructor as any)?.name || 'Unknown',
      slideCount: totalSlides,
      estimatedMinutes: Math.max(1, Math.ceil(totalSlides * 0.7)),
      parentCourseId: meta.parentCourseId,
      parentCourseTitle: undefined,
      utilityMetadata: {
        seoTitle: meta.seoTitle || data.title,
        seoDescription: meta.seoDescription || data.description,
        targetKeywords: meta.targetKeywords || [],
        parentCourseId: meta.parentCourseId,
        ctaText: meta.ctaText || 'Get the Full Course',
        ctaDescription: meta.ctaDescription || 'Go deeper with our comprehensive course',
        leadMagnet: meta.leadMagnet,
        ogImage: meta.ogImage || data.thumbnail_url,
        schemaType: meta.schemaType || 'HowTo',
      },
      sections: sections.map((section: any) => ({
        id: section.id,
        title: section.title,
        order: section.order,
        lessons: (section.lessons || [])
          .sort((a: any, b: any) => a.order - b.order)
          .map((lesson: any) => ({
            id: lesson.id,
            title: lesson.title,
            type: lesson.type,
            duration: lesson.duration,
            isPreview: true, // All utility page lessons are public
            order: lesson.order,
          })),
      })),
      lessonContent,
    };
  },

  /**
   * Get utility pages linked to a specific parent course
   */
  async getUtilityPagesByCourse(courseId: string): Promise<UtilityPageSummary[]> {
    const allPages = await publicService.getUtilityPages();
    return allPages.filter(p => p.parentCourseId === courseId);
  },

  /**
   * Get the parent course summary for a utility page (for the bridge CTA)
   */
  async getParentCourse(courseId: string): Promise<PublicCourse | null> {
    const { data, error } = await supabase
      .from('courses')
      .select(`
        id, title, description, thumbnail_url, price, category, level,
        total_students, rating,
        instructor:users!instructor_id(name)
      `)
      .eq('id', courseId)
      .eq('published', true)
      .single();

    if (error) return null;

    return {
      id: data.id,
      title: data.title,
      description: data.description,
      thumbnailUrl: data.thumbnail_url,
      instructorName: (data.instructor as any)?.name || 'Unknown',
      price: data.price,
      category: data.category,
      level: data.level,
      totalStudents: data.total_students,
      rating: data.rating,
    };
  },
};
